import axios, { AxiosError, AxiosResponse } from "axios";
import { jwtData } from "lib/config";
import { resolveApiUrl } from "./apiUrl";
import {
  AxiosAuthError,
  RefreshData,
  cleanErrorObject,
  cleanErrorObjectFromAxiosError,
  clearStoredAuthIfInvalidRefresh,
  hasJWTExpired,
  isAccessTokenExpiringWithinMs,
  setAuthHeader,
  toAxiosAuthPayload,
} from "./apiClient.lib";

declare module "axios" {
  interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

const storageKeys = {
  refreshUrl: jwtData?.refreshUrl ? jwtData.refreshUrl : "admin/refreshToken",
  authToken: jwtData?.authToken ? jwtData.authToken : "auth_token",
  refreshToken: jwtData?.refreshToken ? jwtData.refreshToken : "refresh_token",
  tokenExpiry: jwtData?.tokenExpiry ? jwtData.tokenExpiry : "tokenExpiry",
  refreshExpiry: jwtData?.refreshExpiry ? jwtData.refreshExpiry : "refreshExpiry",
};

const PREEMPTIVE_EXPIRY_BUFFER_MS = 1000;

let refreshInFlight: Promise<void> | null = null;

const postRefreshAndPersist = async (): Promise<void> => {
  const jwtToken = localStorage.getItem(storageKeys.refreshToken);

  if (!jwtToken) {
    throw new Error("no refresh token");
  }

  const result = await axios.post<any, AxiosResponse<RefreshData>>(
    resolveApiUrl(storageKeys.refreshUrl),
    { refreshToken: jwtToken }
  );

  if (result?.data?.auth?.token) {
    localStorage.setItem(storageKeys.authToken, result.data.auth.token);

    if (result.data.auth.expiresAt != null) {
      localStorage.setItem(storageKeys.tokenExpiry, String(result.data.auth.expiresAt));
    }

    if (result.data.refresh.token) {
      localStorage.setItem(storageKeys.refreshToken, result.data.refresh.token);
    }

    if (result.data.refresh.expiresAt != null) {
      localStorage.setItem(storageKeys.refreshExpiry, String(result.data.refresh.expiresAt));
    }

    return;
  }

  throw new Error("Auth Token Empty");
};

const executeLockedRefresh = async (): Promise<void> => {
  try {
    await postRefreshAndPersist();
  } catch (err) {
    clearStoredAuthIfInvalidRefresh(err, storageKeys);
    throw err;
  } finally {
    refreshInFlight = null;
  }
};

const runSharedRefresh = async (): Promise<void> => {
  if (refreshInFlight) {
    await refreshInFlight;
    return;
  }

  refreshInFlight = executeLockedRefresh();
  await refreshInFlight;
};

const preemptiveRefreshIfNeeded = async (): Promise<void> => {
  if (!localStorage.getItem(storageKeys.refreshToken)) return;

  const tokenExpiry = localStorage.getItem(storageKeys.tokenExpiry) || undefined;
  
  if (!isAccessTokenExpiringWithinMs(PREEMPTIVE_EXPIRY_BUFFER_MS, tokenExpiry)) return;

  try {
    await runSharedRefresh();
  } catch {
    // Let the request proceed; reactive refresh may still apply.
  }
};

const apiClient = axios.create();

apiClient.interceptors.request.use(async (config) => {
  await preemptiveRefreshIfNeeded();
  setAuthHeader(config, storageKeys.authToken);
  return config;
});

apiClient.interceptors.response.use(
  async (response) => {
    if (response.status === 200 && (response.data as any)?.status === "jwt expired") {
      const config = response.config;
      if (config._retry) {
        return Promise.reject(
          cleanErrorObject({
            data: response.data,
            status: response.status,
            statusText: response.statusText,
            original: response,
          })
        );
      }
      config._retry = true;
      try {
        await runSharedRefresh();
      } catch (e) {
        return Promise.reject(
          axios.isAxiosError(e) ? cleanErrorObjectFromAxiosError(e) : cleanErrorObject({
            data: (e as any)?.message,
            status: 0,
            statusText: String(e),
            original: e,
          })
        );
      }
      setAuthHeader(config, storageKeys.authToken);
      return apiClient.request(config);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config;
    if (!originalRequest) {
      return Promise.reject(cleanErrorObjectFromAxiosError(error));
    }
    if (originalRequest._retry) {
      return Promise.reject(cleanErrorObjectFromAxiosError(error));
    }

    const payload = toAxiosAuthPayload(error);
    const jwtToken = localStorage.getItem(storageKeys.refreshToken);

    if (!hasJWTExpired(payload) || !jwtToken) {
      return Promise.reject(cleanErrorObjectFromAxiosError(error));
    }

    originalRequest._retry = true;
    try {
      await runSharedRefresh();
    } catch (e) {
      return Promise.reject(
          axios.isAxiosError(e) ? cleanErrorObjectFromAxiosError(e) : cleanErrorObject({
          data: (e as any)?.message,
          status: 0,
          statusText: String(e),
          original: e,
        })
      );
    }

    setAuthHeader(originalRequest, storageKeys.authToken);
    return apiClient.request(originalRequest);
  }
);

const refreshAuthToken = async (err: AxiosAuthError) => {
  const jwtToken = localStorage.getItem(storageKeys.refreshToken);

  if (hasJWTExpired(err) && jwtToken) {
    try {
      await runSharedRefresh();
    } catch (e) {
      return Promise.reject(e);
    }
    return;
  }
  return Promise.reject(err);
};

export {
  apiClient,
  cleanErrorObject,
  cleanErrorObjectFromAxiosError,
  hasJWTExpired,
  refreshAuthToken,
};
